import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Grid, MenuItem, FormControl, InputLabel, Select, Tooltip, Card,
  CardContent, Alert, CircularProgress, Divider, Snackbar, Tabs, Tab, Autocomplete,
  Link, LinearProgress, useMediaQuery, useTheme
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
  Event as EventIcon, Today as TodayIcon, VideoCall as VideoCallIcon,
  Cancel as CancelIcon, People as PeopleIcon, Link as LinkIcon
} from '@mui/icons-material';
import { meetingService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MEETING_TYPES = ['general', 'internal', 'client', 'interview', 'review', 'production'];
const TYPE_COLORS = { general: 'default', internal: 'info', client: 'primary', interview: 'secondary', review: 'warning', production: 'success' };

const REMINDER_OPTIONS = [
  { label: '10 minutes before', value: 10 },
  { label: '30 minutes before', value: 30 },
  { label: '1 hour before', value: 60 },
  { label: '3 hours before', value: 180 },
  { label: '1 day before', value: 1440 },
  { label: '2 days before', value: 2880 },
];

// datetime-local uses local time; convert to/from for the inputs
const toLocalInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const off = dt.getTimezoneOffset() * 60000;
  return new Date(dt.getTime() - off).toISOString().slice(0, 16);
};

const emptyForm = () => {
  const start = new Date(Date.now() + 60 * 60 * 1000);
  const end = new Date(Date.now() + 90 * 60 * 1000);
  return {
    title: '', description: '', location: '', meeting_type: 'general',
    start_time: toLocalInput(start), end_time: toLocalInput(end),
    timezone: 'Asia/Kolkata', reminder_minutes: [1440, 60, 10],
    attendees: [], externalEmails: '', create_meet: true,
  };
};

const Meetings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const roles = new Set([user?.role, ...(Array.isArray(user?.roles) ? user.roles : [])].filter(Boolean));
  const isManager = roles.has('owner') || roles.has('manager');

  const [scope, setScope] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [staff, setStaff] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await meetingService.getAll({ scope });
      setRows(res.data.meetings || []);
      setGoogleConfigured(!!res.data.google_configured);
    } catch (e) {
      showToast('Failed to load meetings', 'error');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadSummary = useCallback(async () => {
    try { const res = await meetingService.getSummary(); setSummary(res.data); } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSummary();
    meetingService.getAttendeeOptions().then((r) => setStaff(r.data.users || [])).catch(() => {});
  }, [loadSummary]);

  const openCreate = () => { setSelected(null); setForm(emptyForm()); setFormError(''); setOpenForm(true); };

  const openEdit = (m) => {
    setSelected(m);
    const nonOrganizerStaff = (m.attendees || []).filter((a) => !a.isOrganizer && a.userId)
      .map((a) => staff.find((s) => s.id === a.userId)).filter(Boolean);
    const externals = (m.attendees || []).filter((a) => !a.isOrganizer && !a.userId).map((a) => a.email);
    setForm({
      title: m.title || '', description: m.description || '', location: m.location || '',
      meeting_type: m.meetingType || 'general',
      start_time: toLocalInput(m.startTime), end_time: toLocalInput(m.endTime),
      timezone: m.timezone || 'Asia/Kolkata',
      reminder_minutes: Array.isArray(m.reminderMinutes) ? m.reminderMinutes : [60],
      attendees: nonOrganizerStaff, externalEmails: externals.join(', '), create_meet: true,
    });
    setFormError('');
    setOpenForm(true);
  };

  const setField = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }));

  const buildPayload = () => {
    const externals = (form.externalEmails || '').split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
      .map((email) => ({ email }));
    const staffAttendees = (form.attendees || []).map((u) => ({ user_id: u.id }));
    return {
      title: form.title, description: form.description, location: form.location,
      meeting_type: form.meeting_type,
      start_time: new Date(form.start_time).toISOString(),
      end_time: new Date(form.end_time).toISOString(),
      timezone: form.timezone,
      reminder_minutes: form.reminder_minutes,
      attendees: [...staffAttendees, ...externals],
      create_meet: form.create_meet,
    };
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Title is required.'); return; }
    if (!form.start_time || !form.end_time) { setFormError('Start and end time are required.'); return; }
    if (new Date(form.end_time) <= new Date(form.start_time)) { setFormError('End time must be after start time.'); return; }
    setSaving(true); setFormError('');
    try {
      if (selected) { await meetingService.update(selected.id, buildPayload()); showToast('Meeting updated'); }
      else { await meetingService.create(buildPayload()); showToast('Meeting scheduled'); }
      setOpenForm(false); load(); loadSummary();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to save meeting');
    } finally { setSaving(false); }
  };

  const openDetail = async (m) => {
    try { const res = await meetingService.getById(m.id); setSelected(res.data.meeting); setOpenView(true); }
    catch (e) { showToast('Failed to load meeting', 'error'); }
  };

  const handleCancel = async (m) => {
    if (!window.confirm(`Cancel "${m.title}"? Attendees will be notified.`)) return;
    try { await meetingService.cancel(m.id); showToast('Meeting cancelled'); load(); loadSummary(); setOpenView(false); }
    catch (e) { showToast(e.response?.data?.message || 'Failed to cancel', 'error'); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Permanently delete "${m.title}"? This cannot be undone.`)) return;
    try { await meetingService.delete(m.id); showToast('Meeting deleted'); load(); loadSummary(); setOpenView(false); }
    catch (e) { showToast(e.response?.data?.message || 'Failed to delete', 'error'); }
  };

  const fmtWhen = (m) => {
    const s = new Date(m.startTime), e = new Date(m.endTime);
    const opts = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' };
    return `${s.toLocaleString('en-IN', opts)} – ${e.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const reminderLabel = (v) => REMINDER_OPTIONS.find((o) => o.value === v)?.label || `${v} min before`;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Meetings</Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule meetings with reminders{googleConfigured ? ' + Google Meet links' : ''}.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Meeting</Button>
      </Box>

      {!googleConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Google Calendar is not configured — meetings work as an in-app planner with email/WhatsApp reminders.
          Add Google Workspace credentials under Notifications settings to auto-create Google Meet links.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Upcoming', value: summary?.upcoming, icon: <EventIcon />, color: '#1565C0' },
          { label: 'Today', value: summary?.today, icon: <TodayIcon />, color: '#2E7D32' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Card><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
              <Box sx={{ color: s.color }}>{s.icon}</Box>
              <Box><Typography variant="h6" fontWeight={700}>{s.value ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography></Box>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Paper>
        <Tabs value={scope} onChange={(e, v) => setScope(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Upcoming" value="upcoming" />
          <Tab label="Past" value="past" />
          <Tab label="All" value="all" />
        </Tabs>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell>Title</TableCell>
                {!isMobile && <TableCell>When</TableCell>}
                {!isMobile && <TableCell>Type</TableCell>}
                {!isMobile && <TableCell align="center">Attendees</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No meetings.</TableCell></TableRow>
              )}
              {rows.map((m) => (
                <TableRow key={m.id} hover sx={{ opacity: m.status === 'cancelled' ? 0.55 : 1 }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}
                        sx={{ textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>{m.title}</Typography>
                      {m.meetLink && <Tooltip title="Google Meet"><VideoCallIcon fontSize="small" color="success" /></Tooltip>}
                    </Box>
                    {isMobile && <Typography variant="caption" color="text.secondary">{fmtWhen(m)}</Typography>}
                  </TableCell>
                  {!isMobile && <TableCell>{fmtWhen(m)}</TableCell>}
                  {!isMobile && <TableCell><Chip size="small" label={m.meetingType} color={TYPE_COLORS[m.meetingType] || 'default'} /></TableCell>}
                  {!isMobile && <TableCell align="center">{m.attendee_count ?? 0}</TableCell>}
                  <TableCell>
                    <Chip size="small" label={m.status}
                      color={m.status === 'scheduled' ? 'primary' : m.status === 'completed' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View"><IconButton size="small" onClick={() => openDetail(m)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    {m.status === 'scheduled' && (
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(m)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {m.status === 'scheduled' && (
                      <Tooltip title="Cancel"><IconButton size="small" color="warning" onClick={() => handleCancel(m)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {isManager && (
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(m)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selected ? 'Edit Meeting' : 'New Meeting'}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}><TextField label="Title *" fullWidth size="small" value={form.title} onChange={setField('title')} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={form.meeting_type} onChange={setField('meeting_type')}>
                  {MEETING_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField label="Description" fullWidth size="small" multiline rows={2} value={form.description} onChange={setField('description')} /></Grid>
            <Grid item xs={12} md={6}><TextField label="Location" fullWidth size="small" value={form.location} onChange={setField('location')} placeholder="Office / address / online" /></Grid>
            <Grid item xs={12} md={6}><TextField label="Timezone" fullWidth size="small" value={form.timezone} onChange={setField('timezone')} /></Grid>
            <Grid item xs={12} md={6}><TextField label="Start *" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.start_time} onChange={setField('start_time')} /></Grid>
            <Grid item xs={12} md={6}><TextField label="End *" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.end_time} onChange={setField('end_time')} /></Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple size="small" options={staff}
                getOptionLabel={(o) => `${o.fullName || o.username}${o.email ? ` (${o.email})` : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={form.attendees}
                onChange={(e, v) => setForm((s) => ({ ...s, attendees: v }))}
                renderInput={(params) => <TextField {...params} label="Staff attendees" placeholder="Select staff" />}
              />
            </Grid>
            <Grid item xs={12}><TextField label="External attendee emails" fullWidth size="small" value={form.externalEmails} onChange={setField('externalEmails')} placeholder="comma-separated emails" /></Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Reminders</InputLabel>
                <Select
                  multiple label="Reminders" value={form.reminder_minutes}
                  onChange={(e) => setForm((s) => ({ ...s, reminder_minutes: e.target.value }))}
                  renderValue={(sel) => sel.slice().sort((a, b) => b - a).map(reminderLabel).join(', ')}
                >
                  {REMINDER_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {googleConfigured && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Google Meet link</InputLabel>
                  <Select label="Google Meet link" value={form.create_meet ? 'yes' : 'no'} onChange={(e) => setForm((s) => ({ ...s, create_meet: e.target.value === 'yes' }))}>
                    <MenuItem value="yes">Create a Google Meet link</MenuItem>
                    <MenuItem value="no">No video link</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving && <CircularProgress size={16} />}>
            {selected ? 'Update' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View dialog */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.title}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={selected.meetingType} color={TYPE_COLORS[selected.meetingType] || 'default'} />
                <Chip size="small" label={selected.status} color={selected.status === 'scheduled' ? 'primary' : selected.status === 'completed' ? 'success' : 'default'} />
              </Box>
              <Typography variant="body2"><b>When:</b> {fmtWhen(selected)} ({selected.timezone})</Typography>
              {selected.location && <Typography variant="body2"><b>Location:</b> {selected.location}</Typography>}
              {selected.description && <Typography variant="body2"><b>Notes:</b> {selected.description}</Typography>}
              {selected.meetLink && (
                <Typography variant="body2"><VideoCallIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} color="success" />
                  <Link href={selected.meetLink} target="_blank" rel="noopener">Join Google Meet</Link></Typography>
              )}
              {selected.htmlLink && (
                <Typography variant="body2"><LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  <Link href={selected.htmlLink} target="_blank" rel="noopener">Open in Google Calendar</Link></Typography>
              )}
              {selected.googleSyncError && <Alert severity="warning">Google sync: {selected.googleSyncError}</Alert>}
              <Typography variant="body2"><b>Reminders:</b> {(selected.reminderMinutes || []).slice().sort((a, b) => b - a).map(reminderLabel).join(', ') || 'None'}</Typography>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="subtitle2" color="primary"><PeopleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Attendees ({(selected.attendees || []).length})</Typography>
              {(selected.attendees || []).map((a) => (
                <Typography key={a.id} variant="body2">• {a.name || a.email}{a.isOrganizer ? ' (organizer)' : ''} — <span style={{ color: '#888' }}>{a.email}</span></Typography>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Organized by {selected.created_by_name || '—'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selected?.status === 'scheduled' && <Button startIcon={<EditIcon />} onClick={() => { setOpenView(false); openEdit(selected); }}>Edit</Button>}
          {selected?.status === 'scheduled' && <Button color="warning" startIcon={<CancelIcon />} onClick={() => handleCancel(selected)}>Cancel Meeting</Button>}
          {isManager && <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(selected)}>Delete</Button>}
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Meetings;
